import React, { useCallback, useEffect, useState } from "react";
import { Bell, Send, Clock, ShieldAlert, Users, Mail, MessageSquare, RefreshCw, FileText, Trash2, Plus } from "lucide-react";
import { PageHeader, Button, Input, Textarea, Select, Checkbox, StatusBadge, LoadingBlock, EmptyState, toast } from "@/components/ds";
import { api, formatApiError } from "@/lib/api";

const STATUS_TONE: Record<string, "green" | "amber" | "red" | "slate" | "blue"> = {
  delivered: "green", sent: "green", queued: "blue", skipped: "slate", failed: "red",
};
const label = (s: string) => s.replace(/_/g, " ");

export default function Notifications() {
  const [meta, setMeta] = useState<any>(null);
  const [settings, setSettings] = useState<any>(null);
  const [stats, setStats] = useState<Record<string, number>>({});
  const [log, setLog] = useState<any[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);

  const refresh = useCallback(() => {
    api.ntfStats().then(setStats).catch(() => {});
    api.ntfList().then(setLog).catch(() => {});
  }, []);
  useEffect(() => {
    api.ntfMeta().then(setMeta).catch(() => {});
    api.ntfSettings().then(setSettings).catch((e) => toast.error(formatApiError(e)));
    api.ntfTemplates().then(setTemplates).catch(() => {});
    refresh();
  }, [refresh]);

  const save = async (patch: any) => {
    try { setSettings(await api.ntfUpdateSettings(patch)); toast.success("Notification settings saved"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const processDue = async () => {
    try { const r = await api.ntfProcessDue(); toast.success(`Processed ${r.processed} due reminder(s)`); refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!settings || !meta) return <div className="p-2"><LoadingBlock rows={6} /></div>;

  return (
    <div data-testid="page-notifications" className="space-y-5">
      <PageHeader title="Notifications" testid="notifications-header"
        description="Transactional SMS & email for customers and staff — channels, reminders, quiet hours, templates and delivery log."
        action={<Button size="sm" variant="outline" onClick={processDue} data-testid="ntf-process-due"><RefreshCw className="h-4 w-4" /> Process due reminders</Button>} />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5" data-testid="ntf-stats">
        {meta.statuses.map((s: string) => (
          <div key={s} className="rounded-lg border border-slate-200 bg-white p-3 text-center" data-testid={`ntf-stat-${s}`}>
            <p className="text-2xl font-semibold text-slate-800">{stats[s] ?? 0}</p>
            <p className="text-xs capitalize text-slate-400">{s}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <SettingsCard settings={settings} meta={meta} save={save} />
        <StaffRecipientsCard settings={settings} save={save} />
      </div>

      <TypeTogglesCard settings={settings} meta={meta} save={save} />

      <div className="grid gap-4 lg:grid-cols-2">
        <TestSendCard meta={meta} onSent={refresh} />
        <TemplatesCard templates={templates} />
      </div>

      <DeliveryLog log={log} onRefresh={refresh} />

      <EtaNotificationsCard />
    </div>
  );
}

function EtaNotificationsCard() {
  const [cfg, setCfg] = useState<any>(null);
  useEffect(() => { api.etaConfig().then(setCfg).catch(() => setCfg({ included: false })); }, []);
  if (!cfg) return null;
  const save = async (patch: any) => {
    try { setCfg(await api.etaConfigSet(patch)); toast.success("On-the-way settings saved"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const NOTE: string[] = ["en_route", "arrived", "completed"];
  return (
    <Card title="Technician on-the-way notifications" icon={MessageSquare} testid="eta-config">
      {!cfg.included ? (
        <p className="text-sm text-slate-500" data-testid="eta-upgrade">Available on Growth and above. Send customers an approved SMS when the technician is on the way (Pro adds an estimated arrival window).</p>
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <StatusBadge tone="blue">{cfg.plan} plan</StatusBadge>
            {cfg.eta_window ? <StatusBadge tone="green">Arrival window enabled</StatusBadge> : <StatusBadge tone="slate">Basic status SMS</StatusBadge>}
            {cfg.sms_configured ? <StatusBadge tone="green">SMS live</StatusBadge> : <StatusBadge tone="amber">SMS: configuration required</StatusBadge>}
            {cfg.location_aware_available && <StatusBadge tone="slate">Live location: configuration required</StatusBadge>}
          </div>
          <p className="mb-2 text-xs text-slate-400">Approved messages sent to the customer when a technician's job status changes. Variables: {"{first_name} {business} {eta}"}. Opted-out contacts are always excluded.</p>
          <div className="space-y-3">
            {NOTE.map((s) => (
              <div key={s} className="rounded-md border border-slate-100 p-3" data-testid={`eta-status-${s}`}>
                <label className="mb-2 flex items-center justify-between text-sm capitalize text-slate-700">
                  <span>{s.replace("_", " ")} notification</span>
                  <Checkbox checked={!!cfg.notify?.[s]} onChange={(v: boolean) => save({ notify: { ...cfg.notify, [s]: v } })} testid={`eta-notify-${s}`} />
                </label>
                <Textarea defaultValue={cfg.messages?.[s] ?? ""} rows={2} data-testid={`eta-message-${s}`}
                  onBlur={(e) => { if (e.target.value !== cfg.messages?.[s]) save({ messages: { ...cfg.messages, [s]: e.target.value } }); }} />
              </div>
            ))}
          </div>
        </>
      )}
    </Card>
  );
}

function Card({ title, icon: Icon, children, testid, action }: any) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid={testid}>
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-800"><Icon className="h-4 w-4 text-blue-500" /> {title}</div>
        {action}
      </div>
      {children}
    </div>
  );
}
function Field({ label: l, children }: any) {
  return <div className="mb-3"><label className="mb-1 block text-xs font-medium text-slate-500">{l}</label>{children}</div>;
}

function SettingsCard({ settings, meta, save }: any) {
  const ch = ["sms", "email", "both"].map((v) => ({ value: v, label: v }));
  return (
    <Card title="Channels, reminders & quiet hours" icon={Clock} testid="ntf-settings">
      <div className="grid gap-x-6 sm:grid-cols-2">
        <Field label="Customer channel"><Select value={settings.customer_channel} onChange={(v) => save({ customer_channel: v })} testid="ntf-customer-channel" options={ch} /></Field>
        <Field label="Staff channel"><Select value={settings.staff_channel} onChange={(v) => save({ staff_channel: v })} testid="ntf-staff-channel" options={ch} /></Field>
      </div>
      <Field label="Reminder timing (hours before appointment)">
        <Input type="number" defaultValue={settings.reminder_hours_before} id="ntf-reminder" data-testid="ntf-reminder-hours"
          onBlur={(e) => { const v = parseInt(e.target.value); if (v && v !== settings.reminder_hours_before) save({ reminder_hours_before: v }); }} />
      </Field>
      <div className="mt-2 rounded-md border border-slate-100 p-3">
        <label className="flex items-center justify-between text-sm text-slate-700">
          <span className="flex items-center gap-2"><ShieldAlert className="h-4 w-4 text-slate-400" /> Quiet hours</span>
          <Checkbox checked={!!settings.quiet_hours?.enabled} onChange={(v: boolean) => save({ quiet_hours: { ...settings.quiet_hours, enabled: v } })} testid="ntf-quiet-toggle" />
        </label>
        {settings.quiet_hours?.enabled && (
          <div className="mt-2 grid grid-cols-2 gap-3">
            <Field label="Start hour (0-23)"><Input type="number" defaultValue={settings.quiet_hours.start_hour} data-testid="ntf-quiet-start"
              onBlur={(e) => save({ quiet_hours: { ...settings.quiet_hours, start_hour: parseInt(e.target.value) } })} /></Field>
            <Field label="End hour (0-23)"><Input type="number" defaultValue={settings.quiet_hours.end_hour} data-testid="ntf-quiet-end"
              onBlur={(e) => save({ quiet_hours: { ...settings.quiet_hours, end_hour: parseInt(e.target.value) } })} /></Field>
          </div>
        )}
        <label className="mt-2 flex items-center gap-2 text-sm text-slate-600">
          <Checkbox checked={!!settings.emergency_override} onChange={(v: boolean) => save({ emergency_override: v })} testid="ntf-emergency-override" />
          Emergency alerts override quiet hours
        </label>
      </div>
    </Card>
  );
}

function StaffRecipientsCard({ settings, save }: any) {
  const [rows, setRows] = useState<any[]>(settings.staff_recipients?.length ? settings.staff_recipients : [{ name: "", email: "", phone: "" }]);
  useEffect(() => { if (settings.staff_recipients?.length) setRows(settings.staff_recipients); }, [settings.staff_recipients]);
  const set = (i: number, k: string, v: string) => setRows((r) => r.map((row, j) => (j === i ? { ...row, [k]: v } : row)));
  const commit = () => save({ staff_recipients: rows.filter((r) => r.email || r.phone) });
  return (
    <Card title="Staff recipients" icon={Users} testid="ntf-staff-recipients"
      action={<Button size="sm" variant="outline" onClick={() => setRows((r) => [...r, { name: "", email: "", phone: "" }])} data-testid="ntf-add-recipient"><Plus className="h-4 w-4" /></Button>}>
      <p className="mb-2 text-xs text-slate-400">Who receives staff alerts (leads, messages, emergencies, failures).</p>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2" data-testid={`ntf-recipient-${i}`}>
            <Input value={r.name ?? ""} onChange={(e) => set(i, "name", e.target.value)} placeholder="Name" />
            <Input value={r.email ?? ""} onChange={(e) => set(i, "email", e.target.value)} placeholder="Email" data-testid={`ntf-recipient-email-${i}`} />
            <Input value={r.phone ?? ""} onChange={(e) => set(i, "phone", e.target.value)} placeholder="Phone" />
            <Button size="sm" variant="ghost" onClick={() => setRows((rr) => rr.filter((_, j) => j !== i))} data-testid={`ntf-remove-recipient-${i}`}><Trash2 className="h-4 w-4 text-red-400" /></Button>
          </div>
        ))}
      </div>
      <Button size="sm" className="mt-3" onClick={commit} data-testid="ntf-save-recipients">Save recipients</Button>
    </Card>
  );
}

function TypeTogglesCard({ settings, meta, save }: any) {
  const toggle = (t: string, v: boolean) => save({ types: { ...settings.types, [t]: v } });
  const group = (types: string[], title: string, Icon: any) => (
    <div>
      <p className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-400"><Icon className="h-3.5 w-3.5" /> {title}</p>
      <div className="grid gap-2 sm:grid-cols-2">
        {types.map((t) => (
          <label key={t} className="flex items-center gap-2 rounded-md border border-slate-100 px-3 py-2 text-sm capitalize text-slate-700">
            <Checkbox checked={settings.types?.[t] !== false} onChange={(v: boolean) => toggle(t, v)} testid={`ntf-type-${t}`} /> {label(t)}
          </label>
        ))}
      </div>
    </div>
  );
  return (
    <Card title="Notification types" icon={Bell} testid="ntf-types">
      <div className="grid gap-5 md:grid-cols-2">
        {group(meta.customer_types, "Customer", Users)}
        {group(meta.staff_types, "Staff", ShieldAlert)}
      </div>
    </Card>
  );
}

function TestSendCard({ meta, onSent }: { meta: any; onSent: () => void }) {
  const [type, setType] = useState(meta.types[0]);
  const [channel, setChannel] = useState("email");
  const [recipient, setRecipient] = useState("delivered@resend.dev");
  const [result, setResult] = useState<any>(null);
  const send = async () => {
    try { const r = await api.ntfTest({ type, channel, recipient }); setResult(r); onSent();
      toast[r.status === "failed" ? "error" : "success"](`Test ${r.status}`); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Card title="Test send" icon={Send} testid="ntf-test">
      <Field label="Type"><Select value={type} onChange={setType} testid="ntf-test-type" options={meta.types.map((t: string) => ({ value: t, label: label(t) }))} /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Channel"><Select value={channel} onChange={setChannel} testid="ntf-test-channel" options={[{ value: "email", label: "email" }, { value: "sms", label: "sms" }]} /></Field>
        <Field label="Recipient"><Input value={recipient} onChange={(e) => setRecipient(e.target.value)} data-testid="ntf-test-recipient" /></Field>
      </div>
      <Button onClick={send} data-testid="ntf-test-send"><Send className="h-4 w-4" /> Send test</Button>
      {result && (
        <div className="mt-3 rounded-md border border-slate-100 bg-slate-50 p-3" data-testid="ntf-test-result">
          <StatusBadge tone={STATUS_TONE[result.status] ?? "slate"}>{result.status}</StatusBadge>
          <p className="mt-1 text-xs font-medium text-slate-700">{result.subject}</p>
          <p className="text-xs text-slate-500" dangerouslySetInnerHTML={{ __html: result.body }} />
        </div>
      )}
    </Card>
  );
}

function TemplatesCard({ templates }: { templates: any[] }) {
  return (
    <Card title="Templates" icon={FileText} testid="ntf-templates">
      <p className="mb-2 text-xs text-slate-400">Variables: {"{customer_name} {business_name} {service} {appointment_date} {appointment_time} {service_address} {business_phone}"}</p>
      <div className="max-h-72 space-y-2 overflow-y-auto">
        {templates.map((t) => (
          <div key={t.type} className="rounded-md border border-slate-100 p-2" data-testid={`ntf-template-${t.type}`}>
            <div className="flex items-center gap-2"><span className="text-sm font-medium capitalize text-slate-700">{label(t.type)}</span><StatusBadge tone={t.audience === "customer" ? "blue" : "amber"}>{t.audience}</StatusBadge></div>
            <p className="mt-1 text-xs text-slate-500">{t.subject}</p>
          </div>
        ))}
      </div>
    </Card>
  );
}

function DeliveryLog({ log, onRefresh }: { log: any[]; onRefresh: () => void }) {
  return (
    <Card title={`Delivery log (${log.length})`} icon={Bell} testid="ntf-log"
      action={<Button size="sm" variant="outline" onClick={onRefresh} data-testid="ntf-log-refresh"><RefreshCw className="h-4 w-4" /></Button>}>
      {log.length === 0 ? <EmptyState icon={Bell} title="No notifications yet" description="Send a test or trigger a flow to see delivery here." /> : (
        <div className="max-h-96 overflow-y-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-slate-400"><th className="py-1">Type</th><th>Channel</th><th>Recipient</th><th>Status</th><th>When</th></tr></thead>
            <tbody>
              {log.map((n) => (
                <tr key={n.id} className="border-t border-slate-100" data-testid={`ntf-log-row-${n.id}`}>
                  <td className="py-1.5 capitalize text-slate-700">{label(n.type)}</td>
                  <td className="text-slate-500">{n.channel === "email" ? <Mail className="inline h-3.5 w-3.5" /> : <MessageSquare className="inline h-3.5 w-3.5" />} {n.channel}{n.simulated ? " (sim)" : ""}</td>
                  <td className="truncate text-slate-500">{n.recipient || "—"}</td>
                  <td><StatusBadge tone={STATUS_TONE[n.status] ?? "slate"}>{n.status}</StatusBadge>{n.skip_reason ? <span className="ml-1 text-[11px] text-slate-400">{n.skip_reason}</span> : ""}</td>
                  <td className="text-xs text-slate-400">{String(n.created_at).slice(0, 16).replace("T", " ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
